import { Box, SecondaryButton, Text } from "components";
import useTrackPageAnalytics from "hooks/useTrackPageAnalytics";
import useWindowDimensions from "hooks/windowDimensions";
import { useTranslation } from "react-i18next";
import { QRCode } from "react-qrcode-logo";
import { useGetSettingsQuery } from "redux/queries/settings.query";
import { formatQueryString } from "utils/formatQueryStrings";

const EYE_RADIUS = [
  { outer: 30, inner: 0 },
  { outer: 30, inner: 0 },
  { outer: 30, inner: 0 },
];

const QRCard = ({ id, title, url, width, logo, onDownload, downloadLabel }) => (
  <Box backgroundColor="primary" borderRadius="l" p="l">
    <Text
      fontWeight="semibold"
      color="textQuaternary"
      textAlign="center"
      m="s"
      fontSize="xl"
    >
      {title}
    </Text>
    <QRCode
      id={id}
      size={Math.min(width / 2, 400)}
      value={url}
      eyeRadius={EYE_RADIUS}
      enableCORS={true}
      quietZone={width / 50}
      logoWidth={Math.min(width / 8, 70)}
      logoHeight={Math.min(width / 8, 70)}
      logoImage={logo}
    />
    <Box d="flex" justifyContent="center" mT="l">
      <SecondaryButton onClick={() => onDownload(id, title)}>
        <Text fontSize="l" fontWeight="semibold">
          {downloadLabel}
        </Text>
      </SecondaryButton>
    </Box>
  </Box>
);

const SmartGateQR = () => {
  const { t } = useTranslation();

  useTrackPageAnalytics({
    key: "navigate-smart-gate-qr",
    name: "Smart Gate QR Page",
  });

  const { width } = useWindowDimensions();

  const { data: settingsData = {} } = useGetSettingsQuery(undefined, {
    refetchOnReconnect: true,
  });

  const { qr_locations: qrLocations = [] } = settingsData;
  const QrLogo = settingsData.theme?.qr_logo;

  // One QR per touch point; URLs encode source + terminal/area/sub-area for
  // attribution. The qr_locations array is de-duplicated server-side, so touch
  // points that share a QR ("One QR code for both") arrive as a single entry.
  const qrEntries = qrLocations.map(({ name, params = {} }, index) => ({
    id: `smart-gate-${params.terminal}-${params.area}-${params.subArea}-${index}`,
    title: [params.terminal && `Terminal ${params.terminal}`, name]
      .filter(Boolean)
      .join(" · "),
    url:
      window.location.origin +
      "/scan-passport" +
      formatQueryString({
        source: "smart-gate",
        terminal: params.terminal,
        area: params.area,
        subArea: params.subArea,
      }),
  }));

  const downloadQRCode = (id, title) => {
    const canvas = document.getElementById(id);
    if (!canvas) return;

    const pngUrl = canvas
      .toDataURL("image/png")
      .replace("image/png", "image/octet-stream");

    const link = document.createElement("a");
    link.href = pngUrl;
    link.download = `${title}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box
      d="flex"
      direction="column"
      gap="xxl"
      justifyContent="center"
      alignItems="center"
      mV="xxl"
      pB="xxl"
    >
      {qrEntries.map((entry) => (
        <QRCard
          key={entry.id}
          id={entry.id}
          title={entry.title}
          url={entry.url}
          width={width}
          logo={QrLogo}
          onDownload={downloadQRCode}
          downloadLabel={t("qr_pages.download")}
        />
      ))}
    </Box>
  );
};

export default SmartGateQR;
